import type { UseFormReturn } from "react-hook-form";

import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import type { AutoSuggestedWork, GetMyAccountQuery, GetMyProfileQuery, Opportunity } from "@/api/queries";

import { usePostOpportunity } from "@/api/mutations";
import { sendEvent } from "@/helpers/helpers";

import type { OpportunitiesApplyFormData } from "../utils/opportunitiesApplySchema";

import { buildOpportunityPayload } from "../utils/opportunitiesApplyUtils";

type UseOpportunitiesApplyMutationProps = {
  form: UseFormReturn<OpportunitiesApplyFormData>;
  cachedSongs: AutoSuggestedWork[];
  oppsData: Opportunity[];
  myAccount: GetMyAccountQuery;
  userInfo: GetMyProfileQuery;
  setPending: (v: boolean) => void;
  setMutationErrorMessage: (v: string) => void;
};

type UseOpportunitiesApplyMutationReturn = {
  applyOpportunity: (data: OpportunitiesApplyFormData) => Promise<void>;
};

export function useOpportunitiesApplyMutation({
  form,
  cachedSongs,
  oppsData,
  myAccount,
  userInfo,
  setPending,
  setMutationErrorMessage,
}: UseOpportunitiesApplyMutationProps): UseOpportunitiesApplyMutationReturn {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const createMutation = usePostOpportunity();

  const applyOpportunity = async (data: OpportunitiesApplyFormData): Promise<void> => {
    setPending(true);
    setMutationErrorMessage("");
    form.clearErrors("root");

    try {
      createMutation.reset();
      const payload = buildOpportunityPayload(
        data,
        cachedSongs,
        oppsData,
        myAccount.data,
        userInfo.data,
      );

      const response = await createMutation.mutateAsync(payload);

      if (response.failedvalidation === true) {
        const rawMessages = response.messages
          ?? (response.data as { messages?: unknown } | undefined)?.messages;
        const messages = Array.isArray(rawMessages)
          ? rawMessages.join(", ")
          : typeof rawMessages === "string"
            ? rawMessages
            : t("common:validation-failed", "Validation failed");
        form.setError("root", { message: messages });
        return;
      }

      toast.success(t("other:successfully-submitted-opportunity-toast", "successfully submitted opportunity"));
      void navigate({ to: "/opportunities" });
      sendEvent({ property: "pe14489857_opportunities_applied_for" });
    }
    catch (error) {
      setMutationErrorMessage(error instanceof Error ? error.message : String(error));
    }
    finally {
      setPending(false);
    }
  };

  return { applyOpportunity };
}
